import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type {
  ClientNotificationSettingChange,
  ClientNotificationSettingsResponse,
} from "../../../shared/api-types";
import { authenticatedApi } from "@/lib/api";
import {
  PATIENT_NOTIFICATIONS_QUERY_OPTIONS,
  draftChanges,
  failedSave,
  noticeAfterSave,
  patientNotificationsPath,
  patientNotificationsQueryKey,
  startingDraft,
  type CardNotice,
  type DraftRow,
  type NotificationsDraft,
} from "@/lib/patient-notifications-client";

// One Client's notification choices for the patient page's Notifications card
// (NO-851), from NO-848's GET and PUT
// /api/patients/:patientId/notification-settings. Own lane only (no
// `?rdId=`), the only lane the PUT accepts. Only the card mounts a caller, and
// the page mounts the card only with `notifications` on, so a viewer the flag
// hides never asks.

type View = ClientNotificationSettingsResponse;

export function usePatientNotificationSettings(patientId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = patientNotificationsQueryKey(patientId);
  const path = patientNotificationsPath(patientId);

  const query = useQuery({
    queryKey,
    queryFn: async () => (await authenticatedApi<{ data: View }>(getToken, path)).data,
    ...PATIENT_NOTIFICATIONS_QUERY_OPTIONS,
  });

  const save = useMutation({
    mutationFn: async (changes: ClientNotificationSettingChange[]) =>
      (
        await authenticatedApi<{ data: View }>(getToken, path, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ changes }),
        })
      ).data,
  });

  function replace(view: View): void {
    queryClient.setQueryData(queryKey, view);
  }

  return { query, save, replace };
}

interface OpenEditor {
  /** The choices as they were when Edit opened; versions come from here. */
  opened: View;
  draft: NotificationsDraft;
}

/** The card's read, its editor and its last save's notice. */
export function usePatientNotificationsCard(patientId: string) {
  const { query, save, replace } = usePatientNotificationSettings(patientId);
  const [editor, setEditor] = useState<OpenEditor | null>(null);
  const [notice, setNotice] = useState<CardNotice | null>(null);

  function edit(view: View): void {
    setNotice(null);
    setEditor({ opened: view, draft: startingDraft(view) });
  }

  function cancel(): void {
    setNotice(null);
    setEditor(null);
  }

  function draftRow(key: string, row: DraftRow): void {
    setEditor((open) =>
      open ? { ...open, draft: { ...open.draft, [key]: row } } : open
    );
  }

  function submit(): void {
    if (editor === null) return;

    const { opened } = editor;
    const sent = draftChanges(opened, editor.draft);
    setNotice(null);
    save.mutate(sent, {
      onSuccess: (saved) => {
        replace(saved);
        setEditor(null);
        setNotice(noticeAfterSave(opened, saved, sent));
      },
      onError: (error) => {
        const failure = failedSave(error);

        if (failure.current !== null) {
          replace(failure.current);
          setEditor(null);
        }

        setNotice(failure.notice);
      },
    });
  }

  return {
    query,
    editor,
    notice,
    saving: save.isPending,
    edit,
    cancel,
    draftRow,
    submit,
  };
}
